/** FAQ content grouped by category. `faqCategories` drives the filter tabs. */

export const faqs = [
  // Pickup process
  {
    id: 'f1',
    category: 'Pickup Process',
    question: 'How do I schedule an e-waste pickup for my company?',
    answer:
      'Submit the pickup request form with your company details, waste categories and approximate quantity. Our team verifies the inventory and shares a quotation within 24 hours. Once you approve, a collection slot is confirmed within 48 working hours, along with the vehicle and crew details.',
  },
  {
    id: 'f2',
    category: 'Pickup Process',
    question: 'Is there a minimum quantity for a corporate pickup?',
    answer:
      'For a dedicated vehicle we recommend a minimum of 100 kg or roughly 25 assets. Smaller lots are still collected — they are consolidated into a scheduled route for your city, which may add two to three days to the pickup date.',
  },
  {
    id: 'f3',
    category: 'Pickup Process',
    question: 'Do you handle packing and loading at our premises?',
    answer:
      'Yes. Our crew brings sealed bins and packaging material, counts and tags every asset in front of your representative, seals the containers and loads them. You receive a signed chain-of-custody manifest before the vehicle leaves your gate.',
  },

  // Accepted materials
  {
    id: 'f4',
    category: 'Accepted Materials',
    question: 'What types of e-waste do you accept?',
    answer:
      'All categories listed under the E-Waste (Management) Rules, 2022 — IT and telecom equipment, consumer electronics, large and small appliances, medical devices, lab instruments, networking hardware, UPS systems, cabling, batteries and printed circuit boards.',
  },
  {
    id: 'f5',
    category: 'Accepted Materials',
    question: 'Do you accept lithium-ion and lead-acid batteries?',
    answer:
      'Yes, under the Battery Waste Management Rules, 2022. Lithium chemistries are transported in thermal-safe packaging and processed in a dedicated handling bay with fire suppression. Recovery percentages are reported to you by chemistry.',
  },
  {
    id: 'f6',
    category: 'Accepted Materials',
    question: 'Are there items you cannot collect?',
    answer:
      'We do not accept radioactive material, biomedical waste, or equipment with intact refrigerant that has not been degassed by a licensed handler. If your lot includes such items, tell us in advance and we will arrange a compliant partner for those specific streams.',
  },

  // Compliance
  {
    id: 'f7',
    category: 'Compliance',
    question: 'What compliance documentation will we receive?',
    answer:
      'Every engagement includes Form 6, a passbook entry against your producer ID, a recycling certificate, the chain-of-custody manifest and the weighbridge slip. Data destruction certificates and EPR credit notes are issued where those services apply.',
  },
  {
    id: 'f8',
    category: 'Compliance',
    question: 'Are you an authorised recycler or an aggregator?',
    answer:
      'We are a CPCB-authorised recycler operating our own 65,000 sq ft facility. Documentation is issued against our own authorisation, which means there is no third party between you and the entity that actually processes the material.',
  },
  {
    id: 'f9',
    category: 'Compliance',
    question: 'Can you support us during a regulatory inspection or audit?',
    answer:
      'Yes. Your named compliance manager maintains an audit-ready repository of every consignment record and will represent the disposal trail during state or central inspections, ESG audits and internal reviews.',
  },

  // EPR Credits
  {
    id: 'f10',
    category: 'EPR Credits',
    question: 'What are EPR credits and do we need them?',
    answer:
      'Under Extended Producer Responsibility, producers carry an annual recycling target based on what they place on the market. EPR credits generated by an authorised recycler are how that target is met. Bulk consumers who are not producers do not carry a target, but still need Form 6 records for their disposals.',
  },
  {
    id: 'f11',
    category: 'EPR Credits',
    question: 'How long do EPR credits take to reflect on the CPCB portal?',
    answer:
      'Credits are typically generated and reconciled on the portal within 10 to 15 days of processing. You receive a credit note along with the reconciliation confirmation, and your quarterly obligation tracker is updated automatically.',
  },
  {
    id: 'f12',
    category: 'EPR Credits',
    question: 'Can you manage our EPR obligation for the full year?',
    answer:
      'Yes. Our EPR desk maps your target at the start of the financial year, plans the quantity to be channelised each quarter, generates credits against your producer ID and files the reconciliation — so the obligation closes without a March scramble.',
  },

  // Recycling certificates
  {
    id: 'f13',
    category: 'Certificates',
    question: 'What does the recycling certificate contain?',
    answer:
      'It lists the consignment reference, the date of processing, the quantity recovered by material stream, our CPCB authorisation number and a declaration of environmentally sound processing. It is the document most ESG and internal audits ask to see.',
  },
  {
    id: 'f14',
    category: 'Certificates',
    question: 'When are certificates issued after a pickup?',
    answer:
      'Compliance documentation is delivered within 7 working days of processing, and certificates follow within 10 to 12 days of collection. If you need a provisional acknowledgement sooner for an audit, we can issue one on request.',
  },

  // Data destruction
  {
    id: 'f15',
    category: 'Data Destruction',
    question: 'How do you guarantee our data is destroyed?',
    answer:
      'Storage media are sanitised using NIST 800-88 Purge or Destroy methods depending on media type — software wiping, degaussing or physical shredding. Every drive is tracked by serial number and appears on the Certificate of Data Destruction issued to you.',
  },
  {
    id: 'f16',
    category: 'Data Destruction',
    question: 'Can we witness the destruction ourselves?',
    answer:
      'Yes. You can send a representative to our facility, request video recording of the destruction run, or book our on-site shredding truck so media never leaves your premises in a readable state.',
  },

  // Corporate pickups
  {
    id: 'f17',
    category: 'Corporate Pickups',
    question: 'Can you collect from multiple locations under one agreement?',
    answer:
      'Yes. Multi-site programmes run under a single master agreement with location-wise manifests and a consolidated compliance report. This is the standard arrangement for our BFSI, retail and telecom clients.',
  },
  {
    id: 'f18',
    category: 'Corporate Pickups',
    question: 'Do you sign NDAs and vendor onboarding paperwork?',
    answer:
      'We do. NDAs, vendor registration, insurance certificates, crew police verification and site-specific safety documentation are all part of our standard onboarding pack.',
  },

  // Turnaround
  {
    id: 'f19',
    category: 'Turnaround',
    question: 'How quickly can a pickup happen in an urgent situation?',
    answer:
      'Standard pickup is within 48 working hours of approval. For office shutdowns, lease exits or audit deadlines we run expedited collections — in metro locations these can be arranged the next working day.',
  },

  // Pricing
  {
    id: 'f20',
    category: 'Pricing',
    question: 'Do we pay you, or do you pay us?',
    answer:
      'It depends on the material. IT assets with recoverable value attract a buy-back payment to you. Mixed or low-value streams such as CRT monitors, cabling and certain batteries carry a processing charge. A category-wise rate card is shared with your quotation, before any commitment.',
  },
  {
    id: 'f21',
    category: 'Pricing',
    question: 'Are compliance documents charged separately?',
    answer:
      'No. Form 6, passbook entries and recycling certificates are issued as standard with every engagement. Only optional services — on-site shredding, witnessed destruction, dedicated dismantling crews — are quoted separately.',
  },

  // Documentation
  {
    id: 'f22',
    category: 'Documentation',
    question: 'How long should we retain disposal records?',
    answer:
      'The rules require records to be preserved for at least three years and produced on inspection. We retain your complete document set for seven years and keep it accessible through your account manager at any time.',
  },
  {
    id: 'f23',
    category: 'Documentation',
    question: 'Can documentation be issued in a specific entity name or GSTIN?',
    answer:
      'Yes. Documents are issued against the exact legal entity, GSTIN and address you specify at the time of the request, which matters when subsidiaries or multiple registrations are involved. Confirm these details in the request form to avoid reissuance.',
  },
];

/** Unique category list, in the order categories first appear above. */
export const faqCategories = faqs.reduce(
  (acc, faq) => (acc.includes(faq.category) ? acc : [...acc, faq.category]),
  []
);
